import React from 'react';
import styled from 'styled-components'

import Utils from './Utils'
import PageWrapper from './PageWrapper';

import { GridspaceLoadingIcon, DeltaUpIcon, DeltaRightIcon} from './Components'
import { HumanFace, SupportAgent, SuccessIcon } from './Components';
import { InstructionDivider, RowFlexDiv, ColFlexDiv } from './Components';
import { BigFigure, InformationItem, MixerlabText, Header, RoleDetailsContainer, SessionPremise, CountdownWrapper } from './Components';

import './Animations.scss'

const RegenerateButton = styled.button`
  display: inline-block;
  padding: 8px 12px;
  color: #204860;
  box-sizing: border-box;
  align-items: center;
  cursor: pointer;
  color: #204860;
  background: white;
  border-style: solid;
  border-color: mix(#204860, white, 16%);
  height: 50px;
  margin-top: 10px;
  font-family: 'Inter';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px; 
  margin-bottom: 20px;

  &:hover {
    cursor: pointer;
    background: #D5DCE07D;
  }
`;

const RoleDiv = styled.div`
  display: inline-block;
  padding: 8px 12px;
  margin: 5px;
  border-radius: 10px;
  border: 2px solid #1f4760;
  font-family: 'Inter';
  font-size: 1em;
  font-weight: bold;
  color: #204860;
  box-shadow: 2px 4px 8px #4F4F4F;
`;

function iframeResizer(frameID) {
  const frame = document.getElementById(frameID);

  function checkFrameSize() {
    if (frame.contentDocument) {
      const newHeight = frame.contentDocument.body.scrollHeight + "px";
      if (frame.style.height != newHeight) {
        console.log(`Resizing frame to: ${newHeight}`)
        frame.style.height = newHeight;
      }
    }
    requestAnimationFrame(checkFrameSize);
  }

  // Kick-off the frame size callbacks.
  checkFrameSize();
}


class ActorSession extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      role: 'agent',
      sessionPremise: '',
      sessionCard: '',
      instructions: '',
      flexibility: 1,
      feedback: '',
      feedbackChange: null,
      generatingScene: true,
      remainingMinutes: null,
      num_accepted: 0,
      num_rejected: 0,
      num_in_progress: 0,
      refreshCount: 0,
      detailedFeedbackView: false
    };
    this.handleRegenerateClick = this.handleRegenerateClick.bind(this);
  }

  componentDidMount() {
    this.setState({
      role: this.props.role,
      sessionPremise: this.props.sessionPremise.replaceAll("&#39;", "\'").replaceAll("&#34;", "\""),
      instructions: this.props.instructions.replaceAll("&#39;", "\'").replaceAll("&#34;", "\""),
      sessionCard: this.props.sessionCard,
      flexibility: this.props.flexibility,
      feedback: this.props.feedback,
      num_accepted: this.props.num_accepted,
      num_rejected: this.props.num_rejected,
      num_in_progress: this.props.num_in_progress,
      generatingScene: false,
    })
    this.updateState();
  }

  componentDidUpdate(prevProps) {
    if(prevProps.feedback !== this.props.feedback) {
      this.setState({feedback: this.props.feedback});
    };
    if(prevProps.num_accepted !== this.props.num_accepted) {
      this.setState({num_accepted: this.props.num_accepted});
    };
    if(prevProps.num_rejected !== this.props.num_rejected) {
      this.setState({num_rejected: this.props.num_rejected});
    };
    if(prevProps.num_in_progress !== this.props.num_in_progress) {
      this.setState({num_in_progress: this.props.num_in_progress});
    }
  }

  updateState() {
    Utils.apiGet(
      '/actor/session_updates/actor').then((response) => {
        let feedbackChange = null
        if (response.data.num_accepted !== this.state.num_accepted) {
          feedbackChange = true
        }
        this.setState({'feedback': response.data.feedback,
                      'remainingMinutes': response.data.remaining_minutes,
                      'num_accepted': response.data.num_accepted,
                      'num_rejected': response.data.num_rejected,
                      'num_in_progress': response.data.num_in_progress,
                      'feedbackChange': feedbackChange
                    })

    }).then(() => {this.updateStatePeriodically()});
  }

  updateStatePeriodically() {
    if (this.periodicStatusCheck) {
      window.clearTimeout(this.periodicStatusCheck);
      this.periodicStatusCheck = null;
    }

    this.periodicStatusCheck = window.setTimeout(
      this.updateState.bind(this),
      10000
    );
  }

  handleRegenerateClick() {
    Utils.apiPost('/actor/actor_session_card/'+this.state.role).then((response) => {
      this.setState({refreshCount: this.state.refreshCount + 1})
    }
    )
  }

  renderAgentScene() {
    const flexible_instructions = "When you are finished, hang up and click the 'Next Call' button to get the new script, then call again.";
    const verbatim_instructions = "When you are finished, hang up and call again, enacting the same scene with minimal variation.";
    return(
      <div>
        <RoleDetailsContainer>
          <RowFlexDiv style={{'width': '100%'}}>
            <ColFlexDiv style={{'width': '25%'}}>
              <Header>Role details</Header>
              <SupportAgent/>
              <RoleDiv>AGENT</RoleDiv>
            </ColFlexDiv>         
            <MixerlabText>
              <ul>
                <li>Call <b>+1 (559) 523-4169</b>, using the phone number associated with your mixer account <b>({this.props.user_number})</b>.</li>
                <li>If prompted for a four-digit user code, enter the code <b>{this.props.actor_session_code}</b>.</li>
                <li>Select Agent role <b>(option 1)</b> when prompted for your role.</li>
                <li>Act out the scenario described below when connected with a caller.</li>
                {(this.state.flexibility > 0) && <li>{flexible_instructions}</li>}
                {(this.state.flexibility == 0) && <li>{verbatim_instructions}</li>}
                <li>Please <b>refrain from refreshing the page during a call</b>, as your role and information card might change. Feel free to refresh the page afterwards if you wish to switch roles.</li>
              </ul>
            </MixerlabText>
          </RowFlexDiv>
        </RoleDetailsContainer>
        <InstructionDivider />
        <Header>Agent Scenario</Header>
        {(this.state.flexibility > 0) && <RegenerateButton onClick={e => this.handleRegenerateClick.bind(this)()}>Next Call</RegenerateButton>}
        <MixerlabText>
          {this.state.generatingScene &&
            <ColFlexDiv>
              <GridspaceLoadingIcon/>
              <MixerlabText>Generating your new script...</MixerlabText>
            </ColFlexDiv>
          }
          {!this.state.generatingScene &&
            <iframe
              id="agentIframe"
              key={this.state.refreshCount}
              src="/actor/actor_session_card/agent"
              style={{ "width": "100%", "border": "none", "overflow": "hidden" }}
              onLoad={() => iframeResizer("agentIframe")}
            ></iframe>
          }
        </MixerlabText>
      </div>
    )
  }

  

  renderCallerScene() {
    let flexible_instructions = "When the call is done, hang up and click the 'Next Call' button to get a new scenario, then call again."
    let verbatim_instructions = "Stick to the script with minimal variation. When you are finished, hang up and call again, enacting the same scene."
    return(
      <div>
        <RoleDetailsContainer>
          <RowFlexDiv style={{'width': '100%'}}>
            <ColFlexDiv style={{'width': '25%'}}>
              <Header>Role details</Header>
              <HumanFace/>
              <RoleDiv>CALLER</RoleDiv>
            </ColFlexDiv>         
            <MixerlabText>
              <ul>
                <li>Call <b>+1 (559) 523-4169</b>, using the phone number associated with your mixer account <b>({this.props.user_number})</b>.</li>
                <li>If prompted for a four-digit user code, enter the code <b>{this.props.actor_session_code}</b>.</li>
                <li>Select Caller role <b>(option 2)</b> when prompted for your role.</li>
                <li>Act out the scenario described below when connected with an agent.</li>
                {(this.state.flexibility > 0) && <li>{flexible_instructions}</li>}
                {(this.state.flexibility == 0) && <li>{verbatim_instructions}</li>}
                <li>Please <b>refrain from refreshing the page during a call</b>, as your role and information card might change. Feel free to refresh the page afterwards if you wish to switch roles.</li>
              </ul>
            </MixerlabText>
          </RowFlexDiv>
        </RoleDetailsContainer>
        <InstructionDivider />
        <Header>Caller Scenario</Header>
        {(this.state.flexibility > 0) && <RegenerateButton onClick={e => this.handleRegenerateClick()}>Next Call</RegenerateButton>}
        <MixerlabText>
          {this.state.generatingScene &&
            <ColFlexDiv>
              <GridspaceLoadingIcon/>
              <MixerlabText>Generating your new script...</MixerlabText>
            </ColFlexDiv>
            }
            {!this.state.generatingScene &&
              <iframe
                id="callerIframe"
                key={this.state.refreshCount}
                src="/actor/actor_session_card/caller"
                style={{ "width": "100%", "border": "none", "overflow": "hidden" }}
                onLoad={() => iframeResizer("callerIframe")}
              ></iframe>            }
          </MixerlabText>
      </div>
    )
  }

  renderFeedback() {
    return (
      <div style={{'width': '100%'}}>
        <MixerlabText>
          Please note, due to processing times, it may take up to 5 minutes for each call to be evaluated.
          <RowFlexDiv>
            <ColFlexDiv style={{'alignItems':'start', 'margin': '10px'}}>
              <BigFigure style={{'marginLeft':'10px'}} >
                {this.state.num_accepted}
                </BigFigure>
              <InformationItem>Accepted Conversations</InformationItem>
            </ColFlexDiv>
            <ColFlexDiv style={{'alignItems':'start', 'margin': '10px'}}>
              <BigFigure style={{'marginLeft':'10px'}}>{this.state.num_rejected}</BigFigure>
              <InformationItem>Rejected Conversations</InformationItem>
            </ColFlexDiv>
            <ColFlexDiv style={{'alignItems':'start', 'margin': '10px'}}>
              <BigFigure style={{'marginLeft':'10px'}}>{this.state.num_in_progress}</BigFigure>
              <InformationItem>Conversations Processing</InformationItem>
            </ColFlexDiv>
            </RowFlexDiv>
          {!this.state.detailedFeedbackView && <div style={{'marginLeft': '10px'}} onClick={() => this.setState({detailedFeedbackView: true})}>View Conversation Details: <DeltaRightIcon /></div>}
          {this.state.detailedFeedbackView && <div style={{'marginLeft': '10px'}} onClick={() => this.setState({detailedFeedbackView: false})}>Collapse Conversation Details: <DeltaUpIcon /></div>}
          {(this.state.detailedFeedbackView) && 
            <div>
              {this.state.feedback &&
                <ul>
                  {this.state.feedback.split('\n').map((line) => {
                    if (line !== '') {
                      return(<li>{line}</li>)
                    }
                    })}
                </ul>
              }
            </div>
          }
        </MixerlabText>
      </div>
    )
  }

  renderMinutesRemaining() {
    return (
      <div>
        {(this.state.remainingMinutes>0 && this.state.remainingMinutes<=1) && <CountdownWrapper>The current Actor Session will end in 1 minute! Please wrap up your current call, no new calls starting at this time will be recorded.</CountdownWrapper>}
        {(this.state.remainingMinutes<=0) && 
        <div>
          <CountdownWrapper>The current Actor Session has ended, thank you for participating! Please wrap up your current call, no new calls starting at this time will be recorded.</CountdownWrapper>
          <MixerlabText>
            <p>Please return to your <a href="/profile/me">profile</a> to view your payment and performance records for this session. </p>
            <p>Please note, due to processing times, it may take up to 10 minutes for your records to be finalized. </p>
          </MixerlabText>
        </div>
        }
        {(this.state.remainingMinutes>1) && <CountdownWrapper>{this.state.remainingMinutes} Minutes Left</CountdownWrapper>}
      </div>
    )
  }

  render() {
    this.updateStatePeriodically()
    return (
      <PageWrapper isAdmin={this.props.isAdmin}>
        <SessionPremise>{this.state.sessionPremise.toUpperCase()}</SessionPremise>
        {(this.state.remainingMinutes || (this.state.remainingMinutes === 0)) && this.renderMinutesRemaining()}
        {(this.state.remainingMinutes>0) &&
          <div style={{'width': '100%'}}>
            <Header>Feedback</Header>
            {this.renderFeedback()}
            <Header>Session Instructions</Header>
            <MixerlabText>{this.state.instructions}</MixerlabText>
          </div>
        }
        <div>
          {(this.props.role == 'caller') && this.renderCallerScene()}
          {(this.props.role == 'agent') && this.renderAgentScene()}
        </div>
      </PageWrapper>
    );
  }
}

export default ActorSession;
export { RoleDiv, RegenerateButton }
